import PersonaService from 'services/personaService';
import StorageService from 'services/StorageService';
import AccountHistoryService from './account/AccountHistoryService';
import AbstractLocalHistoryService from './local/AbstractLocalHistoryService';
import { ContentBookmarks } from 'types/history';
import LoginRadiusService from 'services/LoginRadiusService';
import { VideoLaneItem } from 'services/cwData';
import { Video } from 'types/api/media';

export type VideoHistory = {
  expireTime: string;
  isComplete: boolean;
  lastModified: number;
  progress: number;
  nextEpisode: NextEpisode | null;
};

export type NextEpisode = {
  guid: string;
  durationSecs: string;
};

export type ShowHistory = {
  videos: {
    [guid: string]: VideoHistory;
  };
  lastWatched: string | null;
  lastWatchedDate: number | null; // Date as number
  wasLoggedIn?: boolean; // flag to check if this item was added after the user logged in
};

type HistoryServiceArgs = {
  storage: StorageService;
  persona: PersonaService | null;
  local: AbstractLocalHistoryService;
  login: LoginRadiusService | null;
};

export default class HistoryService {
  private storage: StorageService;
  private account?: AccountHistoryService;
  private local: AbstractLocalHistoryService;
  private login: LoginRadiusService | null;

  constructor({ storage, persona, local, login }: HistoryServiceArgs) {
    this.storage = storage;

    if (persona) this.account = new AccountHistoryService(persona);
    this.local = local;
    this.login = login;
  }

  async addOrUpdate(
    video: Video | null,
    saveTime: number,
    isComplete: boolean,
    isAdded: boolean,
  ) {
    if (!video) return;
    const loggedIn = this.login?.isLoggedIn();

    if (loggedIn) this.account?.addOrUpdate(video, saveTime, isAdded);

    // we might still add the video locally when logged in, in some cases
    if (!loggedIn || this.local.shouldAlwaysSaveLocally())
      this.local.add(video, saveTime, isComplete, loggedIn);
  }

  async addNextEpisode(video: Video | null) {
    if (!this.login?.isLoggedIn() || !video) return; // don't add next episodes for logged out users

    this.account?.addNextEpisode(video);
  }

  async removeContinueWatchingItems(ids: string[]) {
    return this.getService().removeContinueWatchingItems(ids);
  }

  async getContentBookmarks(slug: string): Promise<ContentBookmarks> {
    return this.getService().getContentBookmarks(slug);
  }

  async getContinueLaneItems(pageSize?: number): Promise<VideoLaneItem[]> {
    return this.getService().getContinueLaneItems(1, pageSize);
  }

  async getVideoHistory(
    showSlug: string,
    guid: string,
  ): Promise<VideoHistory | undefined> {
    return this.getService().getVideoHistory(showSlug, guid);
  }

  async getShowHistory(): Promise<ShowHistory> {
    throw new Error('not implemented');
  }

  async getVideoProgress(
    showSlug: string,
    guid: string,
  ): Promise<number | undefined> {
    return this.getService().getVideoProgress(showSlug, guid);
  }

  async getNextEpisode(showSlug: string): Promise<Video | undefined> {
    return this.getService().getNextEpisode(showSlug);
  }

  shouldUpdateLocalHistoryOnBoot() {
    return this.local.shouldUpdateHistoryOnBoot();
  }

  updateLocalHistoryOnBoot() {
    return this.local.updateHistoryOnBoot();
  }

  shouldSharePlatformHistory(): boolean {
    return this.local.shouldSharePlatformHistory();
  }

  onShareHistoryWithPlatformChanged(newValue: boolean) {
    return this.local.onShareHistoryWithPlatformChanged(newValue);
  }

  private getService(): AccountHistoryService | AbstractLocalHistoryService {
    return this.login?.isLoggedIn() && this.account ? this.account : this.local;
  }
}
